import { useEffect, useState } from 'react';
import { FiSlash } from 'react-icons/fi';
import AdminSectionTitle from '../components/AdminSectionTitle';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import Button from '../components/atoms/Button';
import Checkbox from '../components/atoms/Checkbox';
import CustomModal from '../components/atoms/CustomModal';
import ReferralsTable from '../components/ReferralsTable';
import useGetParams from '../hooks/useGetParams';
import { useGetAllReferralsQuery, useUpdateEventMutation } from '../store';
import FormControlTextArea from '../components/atoms/FormControlTextArea';

const AdminOrderReferralsPage = () => {
  const { businessId } = useGetParams();
  const { event } = useGetSelectedEvent();
  const { data: referrals } = useGetAllReferralsQuery({
    businessId,
    eventId: event?.id || '',
  }, { skip: !event?.id || !businessId });
  const [updateEvent, { isSuccess: isUpdatingEventSuccess }] = useUpdateEventMutation();

  const [termsCheckbox, setTermsCheckbox] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'terms' | 'deactivate'>('terms');

  useEffect(() => {
    if (isUpdatingEventSuccess) {
      window.location.reload();
    }
  }, [isUpdatingEventSuccess]);

  const handleAcceptTerms = () => {
    setIsModalOpen(false);
    updateEvent({
      businessId,
      event: { id: event?.id || '', has_order_referrals: 'enabled' },
    });
  };

  const filteredReferrals = referrals?.filter((fr) => fr.owner_referral_order_id !== null && fr.amount_sold > 0);

  const terms = `Términos y Condiciones – Funcionalidad “Compradores Promotores”

Al activar la funcionalidad de Compradores Promotores en Avantti, el Organizador acepta las siguientes condiciones:

1. Definición de la funcionalidad
La herramienta de Compradores Promotores convierte automáticamente a cada comprador de entradas en un potencial promotor del evento, otorgándole un enlace único para compartir. Las compras realizadas a través de dicho enlace generarán beneficios para el comprador original (“Promotor”).

2. Esquema de devolución
El Organizador reconoce y acepta que, bajo este modelo, Avantti aplicará automáticamente un sistema de devolución sobre el valor de la entrada adquirida por cada Promotor, con los siguientes parámetros:

10% de devolución por cada entrada vendida a través de su enlace.

Escalones de devolución acumulada:

20% al alcanzar 2 entradas vendidas.

50% al alcanzar 5 entradas vendidas.

70% al alcanzar 7 entradas vendidas.

100% al alcanzar 10 entradas vendidas.

La devolución tiene como tope el valor de la entrada original adquirida por el Promotor.

3. Condiciones de validez
Las ventas generadas por un Promotor se computan hasta la hora de inicio del evento.

El cálculo de beneficios se realiza automáticamente según el número total de entradas convertidas desde el enlace único del Promotor.

La devolución aplica únicamente sobre entradas pagas (no gratuitas, invitaciones ni cortesías).

4. Liquidación de devoluciones
Las devoluciones a los Promotores se acreditarán un (1) día hábil después de finalizado el evento.

Avantti realizará la liquidación correspondiente y descontará el monto de devoluciones del total a acreditar al Organizador.

El Organizador acepta que estos montos forman parte del esquema económico del evento y no constituyen costos adicionales por parte de Avantti.

5. Responsabilidades
El Organizador es responsable de comunicar claramente a los compradores/promotores la existencia de este beneficio.

Avantti no se responsabiliza por reclamos de Promotores en caso de que el Organizador no informe, limite o modifique las condiciones ofrecidas.

El Organizador no podrá solicitar cambios en el esquema de devolución una vez iniciado el evento.

6. Limitaciones
La devolución está limitada al valor de la entrada original del Promotor.

Una vez alcanzado el 100% de devolución, el Promotor no podrá acumular beneficios adicionales.

La funcionalidad no aplica a eventos gratuitos ni a entradas no comercializadas dentro de la plataforma Avantti.

7. Aceptación
Al habilitar la funcionalidad de Compradores Promotores, el Organizador declara haber leído, comprendido y aceptado los presentes Términos y Condiciones, y autoriza a Avantti a ejecutar las devoluciones aquí descriptas.
  `;

  return (
        <>
            <div className="flex flex-col gap-8 my-6">
                <AdminSectionTitle title="Eventos" subtitle={`${event?.name} - Compradores promotores`} mainButtons={event?.has_order_referrals === 'enabled' ? [{
                  label: 'Desactivar compradores promotores',
                  onClick: () => {
                    setModalType('deactivate');
                    setIsModalOpen(true);
                  },
                  variant: 'secondary',
                  icon: <FiSlash className="w-4 h-4" />,
                }] : []} />
                {event?.has_order_referrals === 'enabled' ? (
                    <div className="glass-surface">
                        <h1 className="text-2xl font-medium mb-2">Compradores promotores</h1>
                        <ReferralsTable
                            referrals={filteredReferrals || []}
                            eventId={event?.id || ''}
                            businessId={businessId}
                            orderReferrals
                        />
                    </div>
                ) : (
                    <div className="glass-surface max-w-4xl mx-auto">
                        <div className="p-4 mb-6">
                            <p className="text-text-primary text-lg text-center text-xl font-semibold mb-4">
                                Actualmente la función está desactivada
                            </p>
                            <p className="text-text-secondary mb-4">
                                Esta opción permite que tus clientes puedan compartir el evento con un link personalizado.
                                Cada vez que se realiza una compra con ese link, tu cliente suma chances para obtener el
                                reintegro del valor de su entrada. Más información en el siguiente link:
                            </p>

                            <div className="flex justify-center mb-6">
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                      setModalType('terms');
                                      setIsModalOpen(true);
                                    }}
                                    className="flex items-center gap-2"
                                >
                                    <span>Activar Compradores promotores</span>
                                </Button>
                            </div>
                        </div>

                    </div>
                )}
            </div>
            <CustomModal
                isOpen={isModalOpen}
                onRequestClose={() => setIsModalOpen(false)}
                hideCancelButton
                card
            >
                {modalType === 'terms' && (
                    <div className='flex flex-col w-full min-w-[65vw]'>
                        <div className="p-2 w-full">
                            <FormControlTextArea
                                id='terms'
                                label='Terminos'
                                value={terms}
                                rows={15}
                                readOnly
                            />
                        </div>
                        <div className="border-t border-border-primary pt-6 p-4 w-full items-center">
                            <div className="flex items-start gap-3 mb-6 items-center">
                                <Checkbox
                                    isChecked={termsCheckbox}
                                    onCheckChange={setTermsCheckbox}
                                    id="terms-checkbox"
                                    label="He leído y acepto los términos y condiciones."
                                />
                            </div>

                            <div className="flex justify-center">
                                <Button
                                    variant="primary"
                                    onClick={handleAcceptTerms}
                                    disabled={!termsCheckbox}
                                    className="px-8"
                                >
                                    Aceptar y continuar
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                {modalType === 'deactivate' && (
                    <div className="flex flex-col gap-4 p-4 max-w-lg">
                        <h1>Desactivar compradores promotores</h1>
                        <p>Esta acción desactivará la opción de compradores promotores.
                            Esto eliminará la invitación en el ultimo paso del check out y el mail de resumen de compra.
                            Los clientes que ya tienen progreso no podrán seguir juntando ventas para llegar al objetivo.</p>
                        <div className="flex gap-2 justify-center">
                            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button variant="primary" onClick={() => {
                              setIsModalOpen(false);
                              updateEvent({
                                businessId,
                                event: { id: event?.id || '', has_order_referrals: 'disabled' },
                              });
                            }}>
                                Desactivar
                            </Button>
                        </div>
                    </div>
                )}
            </CustomModal>
        </>
  );
};

export default AdminOrderReferralsPage;
